import {
  PwsLoginResponseFragmentFragment,
  PwsUserFragmentFragment,
  PwsBillingInfoFragmentFragment,
  PwsRecurlyBillingInfoInput,
} from "@ihr-radioedit/inferno-webapi";
import {
  ZypeLoginData,
  ZypeUser,
  ZypeTokenResponse,
  ZypeSubscriptionPlan,
  ZypeSubscription,
  RecurlyBillingInfo,
  PwsLoginResponse,
  PwsViewBillingInfoResponse,
  PwsViewSubscriptionResponse,
  PwsViewUserResponse,
  PwsCreateUserResponse,
  PwsSubmittedResponse,
  PwsGiftRecipientResponse,
  PwsPasswordHintResponse,
  PwsCreateSubscriptionPayload,
  PwsCreateUserPayload,
  PwsPresentGiftSubscriptionPayload,
  PwsPresentGiftSubscriptionResponse,
  PWSMemberServiceBase,
  BillingInfoNotFoundError,
} from "./types";
import { PWSGraphQLServiceBase } from "./base-graphql";

const transformUser = (user: PwsUserFragmentFragment | null | undefined): ZypeUser | undefined => {
  if (!user) return undefined;

  const subscription_plans = (user.subscription_plans ?? []).map(p => ({
    ...p,
    id: p.plan_id,
    name: p.plan_name ?? "",
    entitlement_type: p.entitlement_type ?? "global",
    subscription_id: p._id,
  }));

  const subscription_ids = subscription_plans.map(p => p.subscription_id);
  const computed_has_active_subscription = subscription_plans.some(p => (p.status || "").toUpperCase() === "ACTIVE");

  return {
    _id: user._id ?? "",
    amazon_user_id: user.amazon_user_id ?? null,
    birthday: user.birthday ?? null,
    braintree_id: user.braintree_id ?? null,
    created_at: user.created_at ?? "",
    current_sign_in_at: user.current_sign_in_at ?? null,
    current_sign_in_ip: user.current_sign_in_ip ?? null,
    days_trialed: user.days_trialed ?? 0,
    email: user.email ?? "",
    has_trialed: user.has_trialed ?? false,
    last_sign_in_at: user.last_sign_in_at ?? null,
    last_sign_in_ip: user.last_sign_in_ip ?? null,
    name: user.name ?? null,
    pass_count: user.pass_count ?? 0,
    password_token: user.password_token ?? null,
    playlist_count: user.playlist_count ?? 0,
    remember_token: user.remember_token ?? null,
    rss_token: user.rss_token ?? "",
    sex: user.sex ?? null,
    sign_in_count: user.sign_in_count ?? 0,
    site_id: user.site_id ?? "",
    stripe_id: user.stripe_id ?? null,
    subscription_count: user.subscription_count ?? subscription_plans.length,
    terms: user.terms ?? false,
    transaction_count: user.transaction_count ?? 0,
    updated_at: user.updated_at ?? "",
    updates: user.updates ?? false,
    video_count: user.video_count ?? 0,
    linked_devices: user.linked_devices ?? [],
    subscription_plans,
    subscription_ids,
    password_hint: user.password_hint ?? null,
    recurly_code: user.recurly_code ?? null,
    has_active_subscription: user.has_active_subscription ?? computed_has_active_subscription,
    privacy_optout: user.privacy_optout ?? false,
    user_preferences: user.user_preferences ?? {},
  };
};

type GraphQLSubscriptionPlan = PwsUserFragmentFragment["subscription_plans"][number];

const transformInterval = (rawInterval?: string | null): string => {
  let interval = (rawInterval ?? "month").toLowerCase();
  if (interval === "annual") {
    interval = "year";
  } else if (interval === "monthly") {
    interval = "month";
  }
  return interval;
};

const transformSubscription = (sub: GraphQLSubscriptionPlan): ZypeSubscription => {
  return {
    _id: sub._id ?? "",
    _keywords: [],
    amount: (sub.amount ?? "0").toString(),
    cancel_at_period_end: sub.cancel_at_period_end ?? false,
    cancelled_at: sub.cancelled_at ?? null,
    consumer_id: sub.consumer_id ?? "",
    coupon_code: sub.coupon_code ?? null,
    created_at: sub.created_at ?? "",
    currency: sub.currency ?? "USD",
    current_period_end_at: sub.current_period_end_at ?? "",
    current_period_start_at: sub.current_period_start_at ?? "",
    deleted_at: sub.deleted_at ?? null,
    discount_amount: sub.discount_amount ?? null,
    discount_duration: sub.discount_duration ?? null,
    discount_duration_months: sub.discount_duration_months ?? null,
    discount_percent: sub.discount_percent ?? null,
    interval: transformInterval(sub.interval),
    interval_count: sub.interval_count ?? 1,
    last_gateway_sync: sub.last_gateway_sync ?? "",
    last_gateway_sync_error: sub.last_gateway_sync_error ?? null,
    mrr: sub.mrr ?? 0,
    plan_id: sub.plan_id ?? "",
    recurly_token_id: sub.recurly_token_id ?? "",
    recurly_uuid: sub.recurly_uuid ?? "",
    site_id: sub.site_id ?? "",
    start_at: sub.start_at ?? "",
    status: (sub.status ?? "active").toLowerCase(),
    tax_percent: sub.tax_percent ?? null,
    tracking_code: sub.tracking_code ?? null,
    tracking_partner: sub.tracking_partner ?? null,
    trial_allowed: sub.trial_allowed ?? false,
    trial_period_days: sub.trial_period_days ?? 0,
    updated_at: sub.updated_at ?? "",
    plan_name: sub.plan_name ?? "",
    entitlement_type: sub.entitlement_type ?? "global",
    days_remaining: sub.days_remaining ?? 0,
  };
};

const transformBillingInfo = (billingInfo: PwsBillingInfoFragmentFragment): RecurlyBillingInfo => ({
  id: billingInfo.id || undefined,
  object: billingInfo.object || undefined,
  accountId: billingInfo.accountId ?? "",
  primaryPaymentMethod: billingInfo.primaryPaymentMethod ?? false,
  firstName: billingInfo.firstName ?? "",
  lastName: billingInfo.lastName ?? "",
  company: billingInfo.company ?? null,
  address: {
    street1: billingInfo.address?.street1 ?? "",
    street2: billingInfo.address?.street2 || undefined,
    city: billingInfo.address?.city ?? "",
    region: billingInfo.address?.region ?? "",
    postalCode: billingInfo.address?.postalCode ?? "",
    country: billingInfo.address?.country ?? "",
    phone: billingInfo.address?.phone || undefined,
  },
  vatNumber: undefined,
  valid: billingInfo.valid ?? undefined,
  paymentMethod: {
    object: (billingInfo.paymentMethod?.object as RecurlyBillingInfo["paymentMethod"]["object"]) ?? undefined,
    lastFour: billingInfo.paymentMethod?.lastFour ?? "",
    cardType: billingInfo.paymentMethod?.cardType || undefined,
    firstSix: billingInfo.paymentMethod?.firstSix || undefined,
    expMonth: billingInfo.paymentMethod?.expMonth ?? undefined,
    expYear: billingInfo.paymentMethod?.expYear ?? undefined,
    accountType:
      (billingInfo.paymentMethod?.accountType as RecurlyBillingInfo["paymentMethod"]["accountType"]) ?? undefined,
    nameOnAccount: billingInfo.paymentMethod?.nameOnAccount ?? undefined,
    routingNumber: billingInfo.paymentMethod?.routingNumber ?? undefined,
    routingNumberBank: billingInfo.paymentMethod?.routingNumberBank ?? undefined,
  },
  fraud: undefined,
  createdAt: billingInfo.createdAt || undefined,
  updatedAt: billingInfo.updatedAt || undefined,
  updatedBy: billingInfo.updatedBy
    ? { ip: billingInfo.updatedBy.ip ?? "", country: billingInfo.updatedBy.country ?? "" }
    : undefined,
});

type LoginData = PwsLoginResponseFragmentFragment["login"];

const transformLoginData = (data: LoginData): ZypeLoginData => {
  return {
    consumer_id: data.consumer_id ?? "",
    access_token: data.access_token ?? "",
    token_type: data.token_type ?? "Bearer",
    expires_in: data.expires_in ?? 0,
    refresh_token: data.refresh_token ?? "",
    scope: data.scope ?? "consumer",
    created_at: data.created_at ?? 0,
  };
};

export class PWSMemberGraphQLService extends PWSGraphQLServiceBase implements PWSMemberServiceBase {
  constructor(host: string, siteSlug: string, appToken?: string, pwsHost?: string) {
    super(host, siteSlug, appToken, "PWS Member GraphQL Service", pwsHost);
  }

  async login(username: string, password: string): Promise<PwsLoginResponse> {
    return this.withLogging("login", async () => {
      const result = await this.sdk.PWSLogin({
        site: this.show,
        username,
        password,
      });
      const response = result.premiereMember?.login;

      if (!response?.login?.access_token) {
        throw new Error("Login failed - no access token");
      }

      return {
        loginData: transformLoginData(response.login),
        user: transformUser(response.user),
        subscriptions: (response.user?.subscription_plans ?? []).map(transformSubscription),
      };
    });
  }

  async logout(accessToken: string): Promise<void> {
    await this.sdk.PWSLogout({ site: this.show }, this.getAuthHeaders(accessToken));
  }

  async validateToken(accessToken: string, refreshToken: string | null): Promise<ZypeTokenResponse> {
    return this.withLogging("validateToken", async () => {
      const result = await this.sdk.GetPWSToken(
        { site: this.show, refreshToken: refreshToken ?? undefined },
        this.getAuthHeaders(accessToken),
      );

      const tokenResponse = result.pws?.premiereMemberQuery?.token;

      if (!tokenResponse || !tokenResponse.token) {
        throw new Error("Token validation failed - no token response returned");
      }

      const transformedUser = transformUser(tokenResponse.user);
      if (!transformedUser) {
        throw new Error("Token validation failed - no user returned");
      }

      const scopes = tokenResponse.token.scopes ?? ["consumer"];
      const base: ZypeTokenResponse = {
        tokenData: {
          resource_owner_id: tokenResponse.token.resource_owner_id,
          scopes,
          expires_in_seconds: tokenResponse.token.expires_in_seconds,
          application: { uid: tokenResponse.token.application.uid },
          created_at: tokenResponse.token.created_at,
        },
        refreshed: "access_token" in tokenResponse,
        user: transformedUser,
        subscriptions: (tokenResponse.user?.subscription_plans ?? []).map(transformSubscription),
      };

      if ("access_token" in tokenResponse) {
        return {
          ...base,
          accessToken: tokenResponse.access_token,
          refreshToken: tokenResponse.refresh_token,
        };
      }

      return base;
    });
  }

  async listSubscriptionPlans(): Promise<ZypeSubscriptionPlan[]> {
    return this.withLogging("listSubscriptionPlans", async () => {
      const result = await this.sdk.GetPWSSubscriptionPlans({ site: this.show });
      const plans = result.pws?.premiereMemberQuery?.plans ?? [];

      return plans.map(plan => {
        const interval = transformInterval(plan.interval);

        const isGiftPlan = plan.name.toLowerCase().includes("gift");

        return {
          _id: plan._id ?? "",
          _keywords: [] as string[],
          active: plan.active ?? true,
          allow_multiple_subscriptions: false,
          amazon_id: "",
          amount: plan.amount ?? "0",
          apps_only: false,
          braintree_id: null,
          created_at: "",
          currency: plan.currency ?? "USD",
          deleted_at: null,
          description: plan.description ?? "",
          discovery_url: "",
          entitlement_type: plan.entitlement_type ?? "global",
          interval,
          interval_count: plan.interval_count ?? 1,
          is_deleted: false,
          legacy_stripe_id: null,
          marketplace_ids: {},
          name: plan.name ?? "",
          playlist_ids: [] as string[],
          position: 0,
          recurly_code: "",
          site_id: "",
          stripe_id: null,
          third_party_id: isGiftPlan ? "gift" : "",
          trial_period_days: plan.trial_period_days ?? 0,
          updated_at: "",
        };
      });
    });
  }

  async subscribe(accessToken: string, payload: PwsCreateSubscriptionPayload): Promise<PwsViewSubscriptionResponse> {
    return this.withLogging("subscribe", async () => {
      const result = await this.sdk.PWSCreateSubscription(
        {
          site: this.show,
          planId: payload.subscription.plan_id,
          recurlyTokenId: payload.subscription.recurly_token_id,
        },
        this.getAuthHeaders(accessToken),
      );

      const subscription = result.premiereMember?.createSubscription;
      if (!subscription) {
        this.log.error("Create subscription returned null");
        throw new Error("Failed to create subscription");
      }

      return { subscription: transformSubscription(subscription) };
    });
  }

  async presentGiftSubscription(
    accessToken: string,
    payload: PwsPresentGiftSubscriptionPayload,
  ): Promise<PwsPresentGiftSubscriptionResponse> {
    return this.withLogging("presentGiftSubscription", async () => {
      const result = await this.sdk.PWSCreateGiftSubscription(
        {
          site: this.show,
          planId: payload.subscription.plan_id,
          recurlyTokenId: payload.subscription.recurly_token_id,
          recipientId: payload.recipient.id,
        },
        this.getAuthHeaders(accessToken),
      );

      const giftSubscription = result.premiereMember?.createGiftSubscription;
      if (!giftSubscription) {
        this.log.error("Create gift subscription returned null");
        throw new Error("Failed to create gift subscription");
      }

      const donorUser = transformUser(giftSubscription.donorData);
      if (!donorUser) {
        this.log.error("Donor data transformation failed");
        throw new Error("Failed to transform donor data");
      }

      return {
        success: giftSubscription.success ?? false,
        donor: donorUser,
      };
    });
  }

  async cancelSubscription(accessToken: string, subscriptionId: string): Promise<PwsViewSubscriptionResponse> {
    return this.withLogging("cancelSubscription", async () => {
      const { subscriptions: preSubscriptions } = await this.getUser(accessToken, true);
      const existingSub = (preSubscriptions ?? []).find(s => s._id === subscriptionId);

      const result = await this.sdk.PWSCancelSubscription(
        { site: this.show, subscriptionId },
        this.getAuthHeaders(accessToken),
      );
      const success = result.premiereMember?.cancelSubscription;

      if (!success) {
        this.log.error("Cancel subscription returned false");
        throw new Error("Failed to cancel subscription");
      }

      const { subscriptions: postSubscriptions } = await this.getUser(accessToken, true);
      const freshSub = (postSubscriptions ?? []).find(s => s._id === subscriptionId);

      if (freshSub) {
        return { subscription: freshSub };
      }

      if (existingSub) {
        return {
          subscription: {
            ...existingSub,
            status: "canceled",
          },
        };
      }

      this.log.error(`Subscription ${subscriptionId} not found before or after cancellation`);
      throw new Error("Subscription not found after action");
    });
  }

  async reactivateSubscription(accessToken: string, subscriptionId: string): Promise<PwsViewSubscriptionResponse> {
    return this.withLogging("reactivateSubscription", async () => {
      const result = await this.sdk.PWSReactivateSubscription(
        { site: this.show, subscriptionId },
        this.getAuthHeaders(accessToken),
      );

      const subscription = result.premiereMember?.reactivateSubscription;
      if (!subscription) {
        this.log.error("Reactivate subscription returned null");
        throw new Error("Failed to reactivate subscription");
      }

      return { subscription: transformSubscription(subscription) };
    });
  }

  async upDownGradeSubscription(
    accessToken: string,
    subscriptionId: string,
    nextPlanId: string,
  ): Promise<PwsViewSubscriptionResponse> {
    return this.withLogging("upDownGradeSubscription", async () => {
      const result = await this.sdk.PWSChangeSubscriptionPlan(
        { site: this.show, subscriptionId, planId: nextPlanId },
        this.getAuthHeaders(accessToken),
      );

      const changedPlan = result.premiereMember?.changeSubscriptionPlan;
      if (!changedPlan) {
        this.log.error("Change subscription plan returned null");
        throw new Error("Failed to change subscription plan");
      }

      return { subscription: transformSubscription(changedPlan) };
    });
  }

  async verifyEmail(email: string, recaptchaToken: string): Promise<PwsSubmittedResponse> {
    return this.withLogging("verifyEmail", async () => {
      const result = await this.sdk.PWSVerifyEmail({
        site: this.show,
        email,
        recaptcha: recaptchaToken,
      });
      return { submitted: result.premiereMember?.verifyEmail ?? false };
    });
  }

  async getUser(accessToken: string, includeSubscription = true): Promise<PwsViewUserResponse> {
    return this.withLogging("getUser", async () => {
      const result = await this.sdk.GetPWSUser({ site: this.show }, this.getAuthHeaders(accessToken));
      const user = result.pws?.premiereMemberQuery?.user;

      const transformedUser = transformUser(user);
      if (!transformedUser) {
        throw new Error("Failed to get user");
      }

      return {
        user: transformedUser,
        subscriptions: includeSubscription ? (user?.subscription_plans ?? []).map(transformSubscription) : undefined,
      };
    });
  }

  async createUser(emailToken: string, user: PwsCreateUserPayload, doLogin = true): Promise<PwsCreateUserResponse> {
    return this.withLogging("createUser", async () => {
      const result = await this.sdk.PWSCreateUser({
        site: this.show,
        emailToken,
        user: {
          password: user.password,
          name: user.name,
          birthday: user.birthday,
          updates: user.updates,
          terms: user.terms,
          custom: user.custom,
        },
        doLogin,
      });

      const createdUser = result.premiereMember?.createUser;
      const transformedUser = transformUser(createdUser);

      if (!transformedUser) {
        throw new Error("Create user failed - no user returned");
      }

      return {
        user: transformedUser,
        loginData: createdUser?.login ? transformLoginData(createdUser.login) : undefined,
      };
    });
  }

  async getBillingInfo(accessToken: string): Promise<PwsViewBillingInfoResponse> {
    return this.withLogging("getBillingInfo", async () => {
      const result = await this.sdk.GetPWSBillingInfo({ site: this.show }, this.getAuthHeaders(accessToken));

      const billingInfo = result.pws?.premiereMemberQuery?.user?.billing_info;

      if (!billingInfo) {
        throw new BillingInfoNotFoundError();
      }

      return { billingInfo: transformBillingInfo(billingInfo) };
    });
  }

  async updateBillingInfo(
    accessToken: string,
    billingInfo: PwsRecurlyBillingInfoInput,
  ): Promise<PwsViewBillingInfoResponse> {
    return this.withLogging("updateBillingInfo", async () => {
      const result = await this.sdk.PWSUpdateBillingInfo(
        {
          site: this.show,
          billingInfo,
        },
        this.getAuthHeaders(accessToken),
      );

      const updateBillingInfo = result.premiereMember?.updateBillingInfo;
      if (!updateBillingInfo) {
        throw new Error("Failed to update billing info - no data returned");
      }

      return {
        billingInfo: transformBillingInfo(updateBillingInfo),
      };
    });
  }

  async forgotPassword(email: string, recaptchaToken: string): Promise<PwsSubmittedResponse> {
    return this.withLogging("forgotPassword", async () => {
      const result = await this.sdk.PWSForgotPassword({
        site: this.show,
        email,
        recaptcha: recaptchaToken,
      });
      return { submitted: result.premiereMember?.forgotPassword ?? false };
    });
  }

  async resetPassword(password: string, passwordToken: string, passwordHint?: string): Promise<PwsSubmittedResponse> {
    return this.withLogging("resetPassword", async () => {
      const result = await this.sdk.PWSResetPassword({
        site: this.show,
        passwordInfo: {
          password,
          passwordToken,
          passwordHint: passwordHint ?? "",
        },
      });
      return { submitted: result.premiereMember?.resetPassword ?? false };
    });
  }

  async initEmailUpdate(currentEmail: string, newEmail: string, password: string): Promise<PwsSubmittedResponse> {
    return this.withLogging("initEmailUpdate", async () => {
      const result = await this.sdk.PWSInitUpdateEmail({
        site: this.show,
        emailInfo: {
          currentEmail,
          newEmail,
          password,
        },
      });
      return { submitted: result.premiereMember?.initUpdateEmail ?? false };
    });
  }

  async completeEmailUpdate(emailToken: string): Promise<PwsViewUserResponse> {
    return this.withLogging("completeEmailUpdate", async () => {
      const result = await this.sdk.PWSCompleteUpdateEmail({
        site: this.show,
        emailToken,
      });

      const user = result.premiereMember?.completeUpdateEmail?.user;
      const transformedUser = transformUser(user);

      if (!transformedUser) {
        throw new Error("Complete email update failed - no user returned");
      }

      return {
        user: transformedUser,
        subscriptions: (user?.subscription_plans ?? []).map(transformSubscription),
      };
    });
  }

  async updatePassword(
    email: string,
    currentPassword: string,
    newPassword: string,
    passwordHint?: string,
  ): Promise<PwsViewUserResponse> {
    return this.withLogging("updatePassword", async () => {
      const result = await this.sdk.PWSUpdatePassword({
        site: this.show,
        passwordInfo: {
          email,
          currentPassword,
          newPassword,
          passwordHint: passwordHint ?? "",
        },
      });

      const user = result.premiereMember?.updatePassword?.user;
      const transformedUser = transformUser(user);

      if (!transformedUser) {
        throw new Error("Update password failed - no user returned");
      }

      return {
        user: transformedUser,
        subscriptions: (user?.subscription_plans ?? []).map(transformSubscription),
      };
    });
  }

  async getPasswordHint(email: string, recaptchaToken: string): Promise<PwsPasswordHintResponse> {
    return this.withLogging("getPasswordHint", async () => {
      const result = await this.sdk.GetPWSPasswordHint({
        site: this.show,
        email,
        recaptcha: recaptchaToken,
      });
      return { passwordHint: result.pws?.premiereMemberQuery?.passwordHint ?? "" };
    });
  }

  async ensureGiftReceiverAccount(
    email: string,
    recaptchaToken: string,
    accessToken: string,
  ): Promise<PwsGiftRecipientResponse> {
    return this.withLogging("ensureGiftReceiverAccount", async () => {
      const result = await this.sdk.PWSCreateGiftUser(
        {
          site: this.show,
          email,
          recaptcha: recaptchaToken,
          name: "",
        },
        this.getAuthHeaders(accessToken),
      );

      const giftUser = result.premiereMember?.createGiftUser;
      if (!giftUser) {
        this.log.error("Create gift user returned null");
        throw new Error("Failed to create or retrieve gift user");
      }

      return {
        userId: giftUser._id ?? "",
      };
    });
  }
}
